/**
 * Environment detection utilities
 * Determines if the app is running in production, staging, or development
 */

export type Environment = 'production' | 'staging' | 'development';

/**
 * Get the current environment based on API URLs and hostname
 * @returns The current environment
 */
export const getEnvironment = (): Environment => {
  const baseUrl = process.env.REACT_APP_BASE_URL || '';
  const host = window.location.host;

  // Check if staging
  if (
    baseUrl.includes('stg-') ||
    host.includes('stg-') ||
    host.includes('localhost')
  ) {
    return 'staging';
  }

  // Check if production
  if (baseUrl.includes('api.sky360.co.il') && !baseUrl.includes('stg-')) {
    return 'production';
  }

  // Default to development
  return 'development';
};

/**
 * Check if the app is running in production
 * @returns true if in production environment
 */
export const isProduction = (): boolean => getEnvironment() === 'production';

/**
 * Check if the app is running in staging
 * @returns true if in staging environment
 */
export const isStaging = (): boolean => getEnvironment() === 'staging';

/**
 * Check if the app is running in development
 * @returns true if in development environment
 */
export const isDevelopment = (): boolean => getEnvironment() === 'development';

/**
 * Conditional console.log that only logs in staging/development
 * @param args Arguments to pass to console.log
 */
export const devLog = (...args: any[]): void => {
  if (!isProduction()) {
    console.log(...args);
  }
};

/**
 * Conditional console.warn that only logs in staging/development
 * @param args Arguments to pass to console.warn
 */
export const devWarn = (...args: any[]): void => {
  if (!isProduction()) {
    console.warn(...args);
  }
};

/**
 * Conditional console.error that only logs in staging/development
 * @param args Arguments to pass to console.error
 */
export const devError = (...args: any[]): void => {
  if (!isProduction()) {
    console.error(...args);
  }
};
